import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, renderSlot as _renderSlot, createVNode as _createVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, unref as _unref } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "table-custom-mini"
}
const _hoisted_2 = {
  key: 2,
  style: {"width":"26px"}
}
const _hoisted_3 = {
  key: 1,
  class: "table-custom-mini"
}
const _hoisted_4 = { class: "empty" }

import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import TableMenuMini from '@/design-system/molecules/TableMenuMini.vue'
import TableCellData from '@/design-system/atoms/TableCellData.vue'
import TableCellHeading from '@/design-system/atoms/TableCellHeading.vue'
import TableRow from '@/design-system/atoms/TableRow.vue'
import TooltipStandard from '@/design-system/atoms/TooltipStandard.vue'
import { computed, onUnmounted, ref, watch } from 'vue'

export type TableItem = {
  id: string
  name: string
  entityId?: string
  fileType?: string
  sorting?: string
  active?: boolean
}

type Props = {
  fields: Array<any>
  hasArchiveButton?: boolean
  hasCreateButton?: boolean
  hasDeleteButton?: boolean
  hasDownloadButton?: boolean
  hasEditButton?: boolean
  hasMenu?: boolean
  hasSorting?: boolean
  items: TableItem[]
  itemType: string
  selectedId: string
  wideSecondColumn?: boolean
}
type Emits = {
  (e: 'archiveItem'): void
  (e: 'deleteItem'): void
  (e: 'downloadItem'): void
  (e: 'editItem', item: TableItem): void
  (e: 'loadMore'): void
  (e: 'menuClosed'): void
  (e: 'menuOpened', id: string): void
  (e: 'onClickedRow', item: TableItem): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'TableCustomMini',
  props: {
    fields: {},
    hasArchiveButton: { type: Boolean },
    hasCreateButton: { type: Boolean },
    hasDeleteButton: { type: Boolean },
    hasDownloadButton: { type: Boolean },
    hasEditButton: { type: Boolean },
    hasMenu: { type: Boolean, default: true },
    hasSorting: { type: Boolean },
    items: {},
    itemType: {},
    selectedId: {},
    wideSecondColumn: { type: Boolean }
  },
  emits: ["archiveItem", "deleteItem", "downloadItem", "editItem", "loadMore", "menuClosed", "menuOpened", "onClickedRow"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props
const emits = __emit

const { t } = useI18n()
const store = useStore()

const numberOfMenuButtons = computed(() => {
  let count = 0
  if (props.hasArchiveButton) count++
  if (props.hasDeleteButton) count++
  if (props.hasDownloadButton) count++
  if (props.hasEditButton) count++
  return count
})
const hoveredTitle = ref<string>('')
const infiniteList = ref()
const lastMousePosition = ref([0, 0])
const menuButton = ref<HTMLElement | null>(null)
const menuButtonPosition = ref<{
  x: number
  y: number
} | null>(null)
const showMenu = ref(false)
const showTooltip = ref(false)
const trackedMouseEvent = ref<Object>()

function closeMenu() {
  emits('menuClosed')
  menuButton.value = null
  showMenu.value = false
}
function openMenu(item: TableItem) {
  emits('menuOpened', item.id)
  menuButton.value = document.getElementById(item.id)
  showMenu.value = true
}
function setMenuButtonPosition() {
  if (!menuButton.value) return
  const rect = menuButton.value.getBoundingClientRect()
  const bottomMargin = props.hasCreateButton ? 268 : 220 // Lower value is when the bottom container is hidden
  const bottomMarginMobile = numberOfMenuButtons.value > 2 ? 212 : 180
  const leftDashboardMargin = 96
  const topDashboardMargin = 80
  const bottomMax = store.state.mobileScreen
    ? window.innerHeight - bottomMarginMobile
    : window.innerHeight - bottomMargin
  const topMax = store.state.mobileScreen ? topDashboardMargin + 100 : topDashboardMargin + 116
  const yPosition =
    rect.bottom - topDashboardMargin > bottomMax
      ? bottomMax
      : rect.bottom - topDashboardMargin < topMax
      ? topMax
      : rect.bottom - topDashboardMargin
  menuButtonPosition.value = {
    x: rect.left - leftDashboardMargin,
    y: yPosition
  }
}

function trackPointer(event, title: string) {
  hoveredTitle.value = title
  const rect = event.currentTarget.getBoundingClientRect()
  if (event) {
    if (
      lastMousePosition.value[0] === event.clientX &&
      lastMousePosition.value[1] === event.clientY
    )
      return
    lastMousePosition.value = [event.clientX, event.clientY]
    trackedMouseEvent.value = {
      x: rect.left + rect.width / 2,
      y: rect.bottom + rect.height / 2
    }
    const targetEl = event.target as HTMLElement
    if (targetEl.firstElementChild) {
      showTooltip.value =
        targetEl.firstElementChild.clientWidth < targetEl.firstElementChild.scrollWidth
    }
  }
}

watch(infiniteList, () => {
  if (infiniteList.value) {
    infiniteList.value.addEventListener('scroll', () => {
      if (menuButton.value) setMenuButtonPosition()
      if (
        infiniteList.value.scrollTop + infiniteList.value.clientHeight >=
        infiniteList.value.scrollHeight
      ) {
        emits('loadMore')
      }
    })
  }
})
watch(
  menuButton,
  () => {
    if (menuButton.value) {
      setMenuButtonPosition()
      window.addEventListener('resize', setMenuButtonPosition)
    }
    if (!menuButton.value) {
      window.removeEventListener('resize', setMenuButtonPosition)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  infiniteList.value?.removeEventListener('scroll', emits('loadMore'))
  infiniteList.value?.removeEventListener('scroll', setMenuButtonPosition)
  window.removeEventListener('resize', setMenuButtonPosition)
})
__expose({
  closeMenu
})

return (_ctx: any,_cache: any) => {
  return (_ctx.items?.length && _ctx.fields?.length)
    ? (_openBlock(), _createElementBlock("table", _hoisted_1, [
        _createElementVNode("thead", null, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.fields, (field) => {
            return (_openBlock(), _createBlock(TableCellHeading, {
              key: field.key
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(field.name), 1)
              ]),
              _: 2
            }, 1024))
          }), 128)),
          _createVNode(TableCellHeading, null, {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "preview-heading")
            ]),
            _: 3
          })
        ]),
        _createElementVNode("tbody", null, [
          _createElementVNode("div", {
            ref_key: "infiniteList",
            ref: infiniteList,
            class: "list-group"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.items, (item) => {
              return (_openBlock(), _createBlock(TableRow, {
                key: item.id,
                class: _normalizeClass({
            'has-sorting': _ctx.hasSorting,
            'has-sorting__wide': _ctx.hasSorting && _ctx.wideSecondColumn,
            selected: item.id === _ctx.selectedId
          }),
                onOnClicked: ($event: any) => (emits('onClickedRow', item))
              }, {
                default: _withCtx(() => [
                  _createVNode(TableCellData, {
                    onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (showTooltip.value = false)),
                    onMouseenter: (e) => trackPointer(e, item.name)
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("p", null, _toDisplayString(item.name), 1),
                      _withDirectives(_createVNode(TooltipStandard, {
                        arrowPosition: "top-center",
                        class: "tooltip",
                        pointerEvent: trackedMouseEvent.value,
                        text: hoveredTitle.value
                      }, null, 8, ["pointerEvent", "text"]), [
                        [_vShow, showTooltip.value]
                      ])
                    ]),
                    _: 2
                  }, 1032, ["onMouseenter"]),
                  (_ctx.hasSorting)
                    ? (_openBlock(), _createBlock(TableCellData, {
                        key: 0,
                        onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (showTooltip.value = false)),
                        onMouseenter: (e) => trackPointer(e, item.sorting ?? '')
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("p", null, _toDisplayString(item.sorting), 1),
                          _withDirectives(_createVNode(TooltipStandard, {
                            arrowPosition: "top-center",
                            class: "tooltip",
                            pointerEvent: trackedMouseEvent.value,
                            text: hoveredTitle.value
                          }, null, 8, ["pointerEvent", "text"]), [
                            [_vShow, showTooltip.value]
                          ])
                        ]),
                        _: 2
                      }, 1032, ["onMouseenter"]))
                    : _createCommentVNode("", true),
                  (_ctx.hasMenu && item.active)
                    ? (_openBlock(), _createBlock(TableCellData, { key: 1 }, {
                        default: _withCtx(() => [
                          _createVNode(ButtonText, {
                            id: item.id,
                            class: "button-no-padding list-group__button",
                            iconLeft: require('@/design-system/assets/icons/Icon-More-vertical.svg'),
                            size: "extra-small",
                            whiteContent: false,
                            onOnClicked: ($event: any) => (openMenu(item))
                          }, null, 8, ["id", "iconLeft", "onOnClicked"])
                        ]),
                        _: 2
                      }, 1024))
                    : (_ctx.hasMenu && !item.active)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_2))
                      : _createCommentVNode("", true),
                  _createVNode(TableCellData, null, {
                    default: _withCtx(() => [
                      (showMenu.value && item.id === _ctx.selectedId)
                        ? (_openBlock(), _createBlock(TableMenuMini, {
                            key: 0,
                            hasArchiveButton: _ctx.hasArchiveButton,
                            hasDeleteButton: _ctx.hasDeleteButton,
                            hasDownloadButton: _ctx.hasDownloadButton,
                            hasEditButton: _ctx.hasEditButton,
                            position: menuButtonPosition.value,
                            numberOfButtons: numberOfMenuButtons.value,
                            onArchive: _cache[2] || (_cache[2] = ($event: any) => (emits('archiveItem'))),
                            onClose: closeMenu,
                            onEdit: ($event: any) => (emits('editItem', item)),
                            onDelete: _cache[3] || (_cache[3] = ($event: any) => (emits('deleteItem'))),
                            onDownload: _cache[4] || (_cache[4] = ($event: any) => (emits('downloadItem')))
                          }, null, 8, ["hasArchiveButton", "hasDeleteButton", "hasDownloadButton", "hasEditButton", "position", "numberOfButtons", "onEdit"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 2
                  }, 1024),
                  _createVNode(TableCellData, null, {
                    default: _withCtx(() => [
                      _renderSlot(_ctx.$slots, "preview", { item: item })
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1032, ["class", "onOnClicked"]))
            }), 128))
          ], 512)
        ])
      ]))
    : (_openBlock(), _createElementBlock("table", _hoisted_3, [
        _createElementVNode("thead", null, [
          _createVNode(TableCellHeading, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(0 + ' ' + _ctx.itemType.toLowerCase()), 1)
            ]),
            _: 1
          })
        ]),
        _createElementVNode("tbody", null, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(TableRow, null, {
              default: _withCtx(() => [
                _createVNode(TableCellData, null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('general__text__no') +
              ' ' +
              _ctx.itemType.toLowerCase() +
              ' ' +
              _unref(t)('general__text__available').toLowerCase()), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]))
}
}

})